import { Directive } from "@app/components/directive";
import { NgComponent } from "@app/di/decorators";
import { Token } from "@app/di/token";
import { Dirs } from "@app/helpers/dirs";
import * as angular from "angular";
import { IListItemScope } from "@app/components/kb-list/kb-list-item.component";

export interface IListScope extends ng.IScope {
    /**
     * If selection is multiple, then holds the selected items
     */
    selectedItems: any[];
    selectedItem: any;
    /**
     * 'cards' makes it wrap boxes (like the media list)
     */
    skin: string;
    /**
     * controls whether the items in the list can be selected.  
     * If true, Each item can individually control whether they are selectable as well
     */
    selectable: boolean;
    /**
     * if multiple simultaneous selections can be made
     */
    multiple: boolean;
    isSelected: ($id: any) => boolean;
    addToSelectedItems: (item: any, doNotAutoRemove?: boolean) => void;
    setSelectedItems: (item: any) => void;
    shiftToSelectedItems: (item: any) => void;

    // internal scope properties
    _selectedListItems: IListItemScope[];
    _selectedListItem: IListItemScope;
    _listItems: IListItemScope[];
}

export interface IListController {
    scope: IListScope;
    registerListItem: (item: IListItemScope) => void;
}

/**
 * kbList
 *
 * to use kbList, you nest kb-list-item directives inside of a kb-list directive:
 * <kb-list>
 *    <kb-list-item ng-repeat="field in model.fields"></kb-list-item>
 * </kb-list>
 *
 */

@NgComponent({
    token: Token.KbList,
    dependencies: [
    ]
})
export class KbList extends Directive {
    constructor() {

        super();

        this.replace = true;
        this.template = Dirs.component("kb-list");
        this.restrict = "AE";
        this.scope = {
            selectedItem: "=",
            selectedItems: "=?",
            skin: "@",
            multiple: "=?",
            selectable: "=?"
        };
        this.transclude = true;

        // add scope defaults to the compile function
        this.compile = (element, atts: any) => {
            if (angular.isUndefined(atts.selectable)) {
                atts.selectable = "true";
            }
        };

        this.controller = [
            "$scope",
            "$element",
            "$attrs",
            "$transclude",
            function(
                $scope: IListScope,
                $element: ng.IAugmentedJQuery,
                $attrs: ng.IAttributes,
                $transclude: ng.ITranscludeFunction) {
                // set the scope of the tree on it's controller so children can access it
                this.scope = $scope;
                $scope._listItems = [];

                $scope.isSelected = $id => {
                    if ($scope.multiple) {
                        return ($scope._selectedListItems.some(li => li.$id == $id));
                    }
                    return ($scope._selectedListItem ? $scope._selectedListItem.$id == $id : false);
                };

                let lastSelectedItem = -1;

                $scope.addToSelectedItems = (item, doNotAutoRemove?: boolean) => {
                    if (!doNotAutoRemove && $scope.selectedItems.indexOf(item) > -1) {
                        $scope.selectedItems.remove(item);
                        return;
                    }
                    $scope.selectedItems.push(item);
                    lastSelectedItem = $scope._listItems.map(i => i.item).indexOf(item);
                };

                $scope.setSelectedItems = item => {
                    $scope.selectedItems.clear();
                    $scope.selectedItems.push(item);
                    lastSelectedItem = $scope._listItems.map(i => i.item).indexOf(item);
                };

                $scope.shiftToSelectedItems = item => {
                    if (lastSelectedItem < 0) {
                        $scope.setSelectedItems(item);
                    }
                    let newIdx = $scope._listItems.map(i => i.item).indexOf(item);
                    let i = (newIdx > lastSelectedItem ? lastSelectedItem + 1 : lastSelectedItem - 1);
                    let check = () => {
                        if (newIdx > lastSelectedItem) return i <= newIdx;
                        return i >= newIdx;
                    };
                    let increment = () => {
                        if (newIdx > lastSelectedItem) i++;
                        else i--;
                    };
                    for (; check(); increment()) {
                        $scope.addToSelectedItems($scope._listItems[i].item, true);
                    }
                    lastSelectedItem = newIdx;
                };

                // all child nodes register themselves with the tree to handle selection and accordion functionality
                this.registerListItem = (listItem: IListItemScope) => {
                    $scope._listItems.push(listItem);
                    listItem.$on("$destroy", event => {
                        $scope._listItems.remove(listItem);
                    });
                };

                let selectFirstItem = () => {
                    if ($scope._listItems.length) {
                        if ($scope.multiple) {
                            $scope._selectedListItems = [$scope._listItems.first()];
                            $scope.selectedItems = [$scope._selectedListItems.first().item];
                        } else {
                            $scope._selectedListItem = $scope._listItems.first();
                            $scope.selectedItem = $scope._selectedListItem.item;
                        }
                    }
                };

                let updateSelectedItem = () => {
                    $scope._selectedListItems = [];
                    $scope._selectedListItem = undefined;

                    if ((!$scope.multiple && !$scope.selectedItem) ||
                        ($scope.multiple && (!$scope.selectedItems ||
                            !$scope.selectedItems.length))
                    ) {
                        selectFirstItem();
                    } else {
                        for (let listItem of $scope._listItems) {
                            if (!$scope.multiple && $scope.selectedItem == listItem.item) {
                                $scope._selectedListItem = listItem;
                            } else if ($scope.multiple && $scope.selectedItems.indexOf(listItem.item) > -1) {
                                $scope._selectedListItems.push(listItem);
                            }
                        }

                        // if listitem is still undefined, the selected item is no longer in the list, so we reset it
                        if ($scope.multiple && !$scope._selectedListItems.length) {
                            $scope.selectedItems = [];
                        } else if (!$scope.multiple && !$scope._selectedListItem) {
                            $scope.selectedItem = undefined;
                        }
                    }
                };
                // if the consumer changes the selected item, we update the selected node
                if ($scope.multiple) {
                    $scope.$watchCollection("selectedItems", () => {
                        updateSelectedItem();
                    });
                } else {
                    $scope.$watch("selectedItem", () => {
                        updateSelectedItem();
                    });
                }
                // nodes might be added after the selectedItem is set by the consumer, so
                // we need to refind the selected node when the nodes collection changes
                $scope.$watchCollection("_listItems", () => {
                    // if (!$scope._selectedListItem) {
                        updateSelectedItem();
                    // }
                });
            }];
    }

}
